import { useState, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Dashboard,
  Inventory2,
  Build,
  Assignment,
  Schedule,
  LocalShipping,
  Business,
  Assessment,
  Settings,
  ExpandLess,
  ExpandMore,
  QrCode2,
  QrCodeScanner,
  Notifications,
  Science,
  Biotech,
  LocalHospital,
  Speed,
  Description,
  EventNote,
  SwapHoriz,
  RequestQuote,
  BuildCircle,
  DirectionsCar,
  DirectionsCarFilled,
  DevicesOther,
  Campaign,
  ReceiptLong,
  NotificationsActive,
  ReportProblem,
  Bloodtype,
  MedicalServices,
  History,
  Category,
  FormatListBulleted,
  AdminPanelSettings,
  People,
  Security,
  ListAlt,
  CalendarMonth,
  MenuBook,
} from '@mui/icons-material'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'

const DRAWER_WIDTH = 260

// Baked in at build time from package.json (see vite.config.js -> __APP_VERSION__).
// Guarded so the sidebar still renders under Vitest, where the global is undefined.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''

const menuItems = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: <Dashboard />,
  },
  {
    title: 'Calendar',
    path: '/calendar',
    icon: <CalendarMonth />,
  },
  {
    title: 'Inventory',
    icon: <DevicesOther />,
    permissions: ['assets.view', 'transfers.view', 'requisitions.view', 'repairs.view', 'vehicles.view', 'vehicle_requests.view'],
    children: [
      { title: 'Scan QR', path: '/assets/scan', icon: <QrCodeScanner />, permission: 'assets.view' },
      { title: 'Asset Register', path: '/assets', icon: <QrCode2 />, permission: 'assets.view' },
      { title: 'Transfers', path: '/assets/transfers', icon: <SwapHoriz />, permission: 'transfers.view' },
      { title: 'Requisitions', path: '/assets/requisitions', icon: <RequestQuote />, permission: 'requisitions.view' },
      { title: 'External Repairs', path: '/assets/external-repairs', icon: <BuildCircle />, permission: 'repairs.view' },
      { title: 'Vehicles', path: '/assets/vehicles', icon: <DirectionsCar />, permission: 'vehicles.view' },
      { title: 'Vehicle Requests', path: '/assets/vehicle-requests', icon: <DirectionsCarFilled />, permission: 'vehicle_requests.view' },
      { title: 'Asset Settings', path: '/assets/settings', icon: <Settings />, permission: 'assets.edit' },
    ],
  },
  {
    title: 'Maintenance',
    icon: <Build />,
    permissions: ['work_orders.view', 'pm.view', 'calibrations.view', 'job_cards.view', 'fault_reports.view'],
    children: [
      { title: 'Fault Reports', path: '/assets/fault-reports', icon: <ReportProblem />, permission: 'fault_reports.view' },
      { title: 'Work Orders', path: '/work-orders', icon: <Assignment />, permission: 'work_orders.view' },
      { title: 'Job Cards', path: '/maintenance/job-cards', icon: <ReceiptLong />, permission: 'job_cards.view' },
      { title: 'PM Schedule', path: '/pm-schedule', icon: <Schedule />, permission: 'pm.view' },
      { title: 'Calibrations', path: '/calibrations', icon: <Speed />, permission: 'calibrations.view' },
    ],
  },
  {
    title: 'Stock',
    icon: <Inventory2 />,
    permissions: ['inventory.view', 'issue_vouchers.view'],
    children: [
      { title: 'All Items', path: '/inventory', icon: <FormatListBulleted />, permission: 'inventory.view' },
      { title: 'Categories', path: '/inventory/categories', icon: <Category />, permission: 'inventory.view' },
      { title: 'Issue Vouchers', path: '/inventory/issue-vouchers', icon: <ListAlt />, permission: 'issue_vouchers.view' },
    ],
  },
  {
    title: 'Vendors',
    icon: <LocalShipping />,
    permissions: ['vendors.view', 'service_contracts.view'],
    children: [
      { title: 'Vendor List', path: '/vendors', icon: <LocalShipping />, permission: 'vendors.view' },
      { title: 'Service Contracts', path: '/contracts', icon: <Description />, permission: 'service_contracts.view' },
    ],
  },
  {
    title: 'Oncology',
    icon: <LocalHospital />,
    permissions: ['equipment_logs.view', 'qa_records.view', 'radiation.sources'],
    children: [
      { title: 'Equipment Logs', path: '/radiation-oncology/equipment-logs', icon: <EventNote />, permission: 'equipment_logs.view' },
      { title: 'QA/QC Records', path: '/radiation-oncology/qa-records', icon: <Science />, permission: 'qa_records.view' },
      { title: 'Radiation Sources', path: '/radiation-oncology/sources', icon: <Biotech />, permission: 'radiation.sources' },
    ],
  },
  // Clinical Results - Hidden for now
  // {
  //   title: 'Clinical Results',
  //   icon: <MedicalServices />,
  //   permissions: ['lab_results.view', 'imaging_results.view'],
  //   children: [
  //     { title: 'Lab Results', path: '/clinical/lab-results', icon: <Bloodtype />, permission: 'lab_results.view' },
  //     { title: 'Imaging Results', path: '/clinical/imaging-results', icon: <Science />, permission: 'imaging_results.view' },
  //   ],
  // },
  {
    title: 'Facilities',
    path: '/facilities',
    icon: <Business />,
    permission: 'facilities.view',
  },
  {
    title: 'Reports',
    path: '/reports',
    icon: <Assessment />,
    permission: 'reports.view',
  },
  {
    title: 'Notifications',
    icon: <Notifications />,
    children: [
      { title: 'My Notifications', path: '/notifications', icon: <NotificationsActive /> },
      { title: 'Broadcasts', path: '/notifications/broadcasts', icon: <Campaign />, permission: 'broadcasts.view' },
    ],
  },
  {
    title: 'Administration',
    icon: <AdminPanelSettings />,
    adminOnly: true,
    children: [
      { title: 'Users', path: '/administration/users', icon: <People />, adminOnly: true },
      { title: 'Sections/Units', path: '/administration/departments', icon: <Business />, adminOnly: true },
      { title: 'Roles', path: '/administration/roles', icon: <Security />, adminOnly: true },
      { title: 'Settings', path: '/administration/settings', icon: <Settings />, adminOnly: true },
      { title: 'Audit Logs', path: '/administration/audit-logs', icon: <History />, adminOnly: true },
    ],
  },
  {
    title: 'Settings',
    path: '/settings',
    icon: <Settings />,
  },
  {
    title: 'Help & Docs',
    path: '/docs',
    icon: <MenuBook />,
  },
]

const Sidebar = ({ open, onClose, onToggle }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState(['Maintenance'])
  const { hasPermission, hasAnyPermission, isAdmin } = useAuth()
  const { orgName, orgLogoUrl } = useBranding()

  // Filter menu items based on user permissions
  const filteredMenuItems = useMemo(() => {
    const filterItem = (item) => {
      // Check adminOnly items first
      if (item.adminOnly && !isAdmin) {
        return null
      }

      if (!item.permission && !item.permissions && !item.adminOnly) {
        return item
      }

      if (item.permission && !hasPermission(item.permission)) {
        return null
      }

      if (item.permissions && !hasAnyPermission(item.permissions)) {
        return null
      }

      if (item.children) {
        const filteredChildren = item.children
          .map(filterItem)
          .filter(Boolean)

        if (filteredChildren.length === 0) {
          return null
        }

        return { ...item, children: filteredChildren }
      }

      return item
    }

    return menuItems.map(filterItem).filter(Boolean)
  }, [hasPermission, hasAnyPermission, isAdmin])

  const handleToggle = (title) => {
    setExpandedItems((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    )
  }

  const isActive = (path) =>
    location.pathname === path ||
    // The Help center owns every /docs/<slug> sub-route.
    (path === '/docs' && location.pathname.startsWith('/docs/'))

  const isParentActive = (children) =>
    children?.some((child) => location.pathname === child.path)

  const handleItemClick = () => {
    if (isMobile) {
      onClose()
    }
  }

  const renderMenuItem = (item, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.title)
    const active = isActive(item.path) || isParentActive(item.children)

    if (hasChildren) {
      return (
        <Box key={item.title}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleToggle(item.title)}
              aria-expanded={isExpanded}
              sx={{
                mx: 1,
                borderRadius: 2,
                mb: 0.5,
                backgroundColor: active ? 'action.selected' : 'transparent',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ color: active ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: active ? 600 : 400,
                  color: active ? 'primary.main' : 'text.primary',
                }}
              />
              {isExpanded ? (
                <ExpandLess sx={{ color: 'text.secondary' }} />
              ) : (
                <ExpandMore sx={{ color: 'text.secondary' }} />
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        </Box>
      )
    }

    return (
      <ListItem key={item.title} disablePadding>
        <ListItemButton
          component={NavLink}
          to={item.path}
          onClick={handleItemClick}
          aria-current={isActive(item.path) ? 'page' : undefined}
          sx={{
            mx: 1,
            ml: depth > 0 ? 4 : 1,
            borderRadius: 2,
            mb: 0.5,
            backgroundColor: isActive(item.path)
              ? 'action.selected'
              : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: isActive(item.path) ? 600 : 400,
              color: isActive(item.path) ? 'primary.main' : 'text.primary',
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Logo/Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          component="img"
          src="/images/duonep-logo.png"
          alt="DuoNep"
          sx={{
            height: 44,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Navigation */}
      <Box component="nav" aria-label="Main navigation" sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <List>
          {filteredMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Footer - organization co-brand */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {orgLogoUrl && (
          <Box textAlign="center" mt={0.5}>
            <Box
              component="img"
              src={orgLogoUrl}
              alt={orgName}
              sx={{ height: 28, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          display="block"
          fontWeight={600}
          mt={0.5}
        >
          {orgName}
        </Typography>
        {APP_VERSION && (
          <Typography
            variant="caption"
            color="text.disabled"
            textAlign="center"
            display="block"
            mt={0.5}
          >
            v{APP_VERSION}
          </Typography>
        )}
      </Box>
    </Box>
  )

  // On mobile, always use temporary drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  // On desktop, use persistent drawer that can be hidden
  return (
    <Drawer
      variant="persistent"
      open={open}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar
